/**
 * PublishBar — top-right controls for the draft → publish workflow.
 *
 * Draft state:
 *   • "clean"     → nothing to save, matches published (green ring)
 *   • "dirty"     → local edits not yet PATCHed (amber dot)
 *   • "drafted"   → PATCHed to draft, awaiting Publish (blue dot)
 *
 * Buttons:
 *   • Save Draft    → PATCH ?draft=true (unpublished)
 *   • Discard Draft → POST /revert (drops server draft, reloads published)
 *   • Publish       → POST /publish (server promotes draft → value)
 *
 * The bar keeps existing "Save & Apply" alive so legacy behaviour is
 * preserved via a single click when the super-admin wants to skip
 * the draft workflow.
 */
import { Loader2, Save, Send, Undo2, CircleDot } from "lucide-react";

export default function PublishBar({
  saving, publishing, reverting,
  dirty, hasServerDraft,
  onSaveDraft, onPublish, onDiscardDraft, onSaveAndApply,
}) {
  const chip =
    dirty          ? { label: "Unsaved", cls: "bg-amber-50 text-amber-700 border-amber-200" } :
    hasServerDraft ? { label: "Draft",   cls: "bg-sky-50 text-sky-700 border-sky-200" } :
                     { label: "Live",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="vb-publish-bar">
      <span
        className={`hidden md:inline-flex items-center gap-1 h-7 px-2 rounded-full border text-[10.5px] font-semibold ${chip.cls}`}
        data-testid="vb-status-chip"
      >
        <CircleDot className="w-3 h-3" />
        {chip.label}
      </span>

      {hasServerDraft && (
        <button
          type="button"
          onClick={onDiscardDraft}
          disabled={reverting}
          className="h-9 px-2.5 md:px-3 rounded-lg text-[12px] font-semibold text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1.5 disabled:opacity-60"
          data-testid="vb-discard-draft"
          title="Discard the current draft and revert to the last published version"
        >
          {reverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
          <span className="hidden md:inline">Discard</span>
        </button>
      )}

      <button
        type="button"
        onClick={onSaveDraft}
        disabled={saving || !dirty}
        className="h-9 px-2.5 md:px-3 rounded-lg text-[12px] font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="vb-save-draft"
        title="Save changes as a draft (customers will NOT see them)"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        <span className="hidden md:inline">Save Draft</span>
      </button>

      <button
        type="button"
        onClick={onPublish}
        disabled={publishing || (!hasServerDraft && !dirty)}
        className="h-9 px-3 md:px-4 rounded-lg text-[12px] font-semibold bg-[#E32636] hover:bg-[#c11e2b] text-white inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="vb-publish"
        title="Publish the current draft to live — customers will see it immediately"
      >
        {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        <span>{publishing ? "Publishing…" : "Publish"}</span>
      </button>

      {/* Legacy one-shot save-and-publish */}
      <button
        type="button"
        onClick={onSaveAndApply}
        disabled={saving || publishing || (!dirty && !hasServerDraft)}
        className="hidden lg:inline-flex h-9 px-2.5 rounded-lg text-[11.5px] font-semibold text-slate-500 hover:text-slate-800 items-center gap-1.5 disabled:opacity-40"
        data-testid="vb-save-apply-legacy"
        title="Save + publish in one shot (legacy behaviour)"
      >
        Apply Now
      </button>
    </div>
  );
}
